import React, { useState, useEffect, useMemo } from 'react';
import { useTaskContext } from '../context/TaskContext';
import { useUserContext } from '../context/UserContext';
import { useStatusContext } from '../context/StatusContext';
import ModalWindow from '../components/ModalWindow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHouse,
  faCalendarDays,
  faChevronLeft,
  faChevronRight,
  faClock,
  faListCheck,
  faCircleCheck,
  faTriangleExclamation,
  faUserPen
} from '@fortawesome/free-solid-svg-icons';

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];
/* -------- Вспомогательные функции -------- */
const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const sameDay = (a, b) => {
  if (!a || !b) return false;
  return startOfDay(a).getTime() === startOfDay(b).getTime();
};

const dayKey = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
};

const formatDate = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d)) return '';
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/* -------- Календарь -------- */
const Calendar = ({ tasks, onSelectDay, selectedDay }) => {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  // Группируем задачи по дням дедлайна
  const tasksByDay = useMemo(() => {
    const map = {};
    tasks.forEach((t) => {
      if (!t.finishDate) return;
      const k = dayKey(t.finishDate);
      if (!map[k]) map[k] = [];
      map[k].push(t);
    });
    return map;
  }, [tasks]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  // Первый день недели (Пн = 0)
  const firstDay = new Date(year, month, 1);
  let startOffset = firstDay.getDay() - 1;
  if (startOffset < 0) startOffset = 6;

  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const today = startOfDay(new Date());

  const prevMonth = () => setCursor(new Date(year, month - 1, 1));
  const nextMonth = () => setCursor(new Date(year, month + 1, 1));
  const goToday = () => {
    const d = new Date();
    setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
  };

  return (
    <div className="dashboard-calendar">
      <div className="dashboard-calendar__header">
        <button className="dashboard-calendar__nav" onClick={prevMonth} title="Предыдущий месяц">
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <div className="dashboard-calendar__title">
          <span>{MONTHS[month]} {year}</span>
          <button className="dashboard-calendar__today" onClick={goToday}>Сегодня</button>
        </div>
        <button className="dashboard-calendar__nav" onClick={nextMonth} title="Следующий месяц">
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>

      <div className="dashboard-calendar__weekdays">
        {WEEKDAYS.map((w) => (
          <div key={w} className="dashboard-calendar__weekday">{w}</div>
        ))}
      </div>

      <div className="dashboard-calendar__grid">
        {cells.map((day, idx) => {
          if (!day) return <div key={`empty-${idx}`} className="dashboard-calendar__cell dashboard-calendar__cell--empty" />;

          const k = dayKey(day);
          const dayTasks = tasksByDay[k] || [];
          const isToday = sameDay(day, today);
          const isSelected = selectedDay && sameDay(day, selectedDay);
          const isPast = day < today && !isToday;
          const hasOverdue = isPast && dayTasks.length > 0;

          return (
            <button
              key={k}
              type="button"
              className={[
                'dashboard-calendar__cell',
                isToday ? 'dashboard-calendar__cell--today' : '',
                isSelected ? 'dashboard-calendar__cell--selected' : '',
                dayTasks.length > 0 ? 'dashboard-calendar__cell--has-tasks' : '',
                hasOverdue ? 'dashboard-calendar__cell--overdue' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => onSelectDay(day)}
              title={dayTasks.length > 0 ? `Задач: ${dayTasks.length}` : undefined}
            >
              <span className="dashboard-calendar__daynum">{day.getDate()}</span>
              {dayTasks.length > 0 && (
                <span className="dashboard-calendar__badge">{dayTasks.length}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* -------- Скелетон -------- */
const DashboardSkeleton = () => (
  <div className="dashboard-skeleton">
    <div className="skeleton skeleton--title" style={{ width: 220, height: 28 }} />
    <div className="dashboard-skeleton__grid">
      <div className="skeleton" style={{ height: 380, borderRadius: 16 }} />
      <div className="skeleton" style={{ height: 380, borderRadius: 16 }} />
    </div>
  </div>
);

/* -------- Страница -------- */
const DashboardPage = ({ externalCreateOpen, onExternalCreateClose }) => {
  const { tasks, fetchTasks, tasksLoading } = useTaskContext();
  const { users, id } = useUserContext();
  const { status } = useStatusContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  

  useEffect(() => {
    
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Новый эффект — реакция на кнопку из Sidebar
  useEffect(() => {
    if (externalCreateOpen) {
      setSelectedTask(null);   // null = создание новой задачи
      setIsModalOpen(true);
      if (onExternalCreateClose) onExternalCreateClose();
    }
  }, [externalCreateOpen, onExternalCreateClose]);

  // Мои задачи
  const myTasks = useMemo(
  () => (tasks || []).filter((t) => {
    const list = Array.isArray(t.executors)
      ? t.executors
      : (t.executor ? [t.executor] : []);
    return list.includes(id);
  }),
  [tasks, id]
);
    const createdTasks = useMemo(
    () => (tasks || []).filter((t) => String(t.creator) === String(id)),
    [tasks, id]
    );

    // сортировка по дате создания (новые сверху)
    const sortedCreatedTasks = useMemo(() => {
    return [...createdTasks].sort((a, b) => {
        const da = a.createDate ? new Date(a.createDate).getTime() : 0;
        const db = b.createDate ? new Date(b.createDate).getTime() : 0;
        return db - da;
    });
    }, [createdTasks]);

  const openTask = (task) => {
    setSelectedTask(task);
    setIsModalOpen(true);
  };
  const closeTask = () => {
    setSelectedTask(null);
    setIsModalOpen(false);
  };

  const getStatusName = (statusId) => {
    const s = status?.find((st) => st._id === statusId);
    return s ? s.name : '—';
  };
  const getStatusColor = (statusId) => {
    const s = status?.find((st) => st._id === statusId);
    return s?.color || '#7c3aed';
  };
  const getUserName = (userId) => {
    const u = users?.find((u) => u._id === userId);
    return u ? u.name : '—';
  };

  const now = new Date();

  // Статистика
  const overdueCount = myTasks.filter(
    (t) => t.status !== "685ab4dcb7aab8740cd7d8f1" && t.finishDate && new Date(t.finishDate) < now
  ).length;

  const todayTasks = myTasks.filter((t) => t.finishDate && sameDay(t.finishDate, now));
  const weekTasks = myTasks.filter((t) => {
    if (!t.finishDate) return false;
    const diff = (startOfDay(t.finishDate) - startOfDay(now)) / 86400000;
    return diff >= 0 && diff <= 7;
  });

  // Задачи для выбранного дня
  const dayTasks = selectedDay
    ? myTasks.filter((t) => t.finishDate && sameDay(t.finishDate, selectedDay))
    : [];

  // Сортируем "мои задачи" по дедлайну
  const sortedMyTasks = useMemo(() => {
    return [...myTasks].sort((a, b) => {
      const da = a.finishDate ? new Date(a.finishDate).getTime() : Infinity;
      const db = b.finishDate ? new Date(b.finishDate).getTime() : Infinity;
      return da - db;
    });
  }, [myTasks]);

  return (
    <div className="crm-page">
      {/* Breadcrumb */}
      <div className="crm-breadcrumb">
        <span className="crm-breadcrumb__item">Рабочий стол</span>
        <span className="crm-breadcrumb__sep">›</span>
        <span className="crm-breadcrumb__item crm-breadcrumb__item--active">Домашняя</span>
      </div>

      {/* Заголовок */}
      <div className="crm-page-header">
        <h1 className="crm-page-title">
          <FontAwesomeIcon icon={faHouse} style={{ marginRight: 10 }} />
          Домашняя
        </h1>
        <span className="dashboard-subtitle">
          Задачи пользователя: <b>{getUserName(id)}</b>
        </span>
      </div>

      {tasksLoading ? (
        <DashboardSkeleton />
      ) : (
        <>
          {/* Статистика */}
          <div className="dashboard-stats">
            <div className="dashboard-stat dashboard-stat--blue">
              <div className="dashboard-stat__icon">
                <FontAwesomeIcon icon={faListCheck} />
              </div>
              <div className="dashboard-stat__body">
                <div className="dashboard-stat__value">{myTasks.length}</div>
                <div className="dashboard-stat__label">Всего моих задач</div>
              </div>
            </div>

            <div className="dashboard-stat dashboard-stat--violet">
              <div className="dashboard-stat__icon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div className="dashboard-stat__body">
                <div className="dashboard-stat__value">{weekTasks.length}</div>
                <div className="dashboard-stat__label">На этой неделе</div>
              </div>
            </div>

            <div className="dashboard-stat dashboard-stat--amber">
              <div className="dashboard-stat__icon">
                <FontAwesomeIcon icon={faCircleCheck} />
              </div>
              <div className="dashboard-stat__body">
                <div className="dashboard-stat__value">{todayTasks.length}</div>
                <div className="dashboard-stat__label">Срок сегодня</div>
              </div>
            </div>

            <div className="dashboard-stat dashboard-stat--red">
              <div className="dashboard-stat__icon">
                <FontAwesomeIcon icon={faTriangleExclamation} />
              </div>
              <div className="dashboard-stat__body">
                <div className="dashboard-stat__value">{overdueCount}</div>
                <div className="dashboard-stat__label">Просрочено</div>
              </div>
            </div>

            <div className="dashboard-stat dashboard-stat--teal">
                <div className="dashboard-stat__icon">
                    <FontAwesomeIcon icon={faUserPen} />
                </div>
                <div className="dashboard-stat__body">
                    <div className="dashboard-stat__value">{createdTasks.length}</div>
                    <div className="dashboard-stat__label">Создано мной</div>
                </div>
            </div>
          </div>

          <div className="dashboard-layout">
            {/* Левая колонка — календарь */}
            <div className="dashboard-panel">
              <div className="dashboard-panel__header">
                <FontAwesomeIcon icon={faCalendarDays} className="dashboard-panel__icon" />
                <span className="dashboard-panel__title">Календарь дедлайнов</span>
              </div>
              <Calendar
                tasks={myTasks}
                selectedDay={selectedDay}
                onSelectDay={(d) => setSelectedDay(sameDay(d, selectedDay) ? null : d)}
              />

              {selectedDay && (
                <div className="dashboard-day">
                  <div className="dashboard-day__title">
                    Задачи на {selectedDay.toLocaleDateString('ru-RU')}
                  </div>
                  {dayTasks.length === 0 ? (
                    <div className="dashboard-day__empty">В этот день дедлайнов нет</div>
                  ) : (
                    <div className="dashboard-day__list">
                      {dayTasks.map((t) => (
                        <div
                          key={t._id}
                          className="dashboard-day__item"
                          onClick={() => openTask(t)}
                        >
                          <span
                            className="dashboard-day__dot"
                            style={{ background: getStatusColor(t.status) }}
                          />
                          <span className="dashboard-day__name">{t.name}</span>
                          <span className="dashboard-day__status">{getStatusName(t.status)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
             {/* 2. Созданные мной — НОВЫЙ БЛОК */}
             {sortedCreatedTasks.length > 0 && (
                <div className="dashboard-panel dashboard-panel--created">
                    <div className="dashboard-panel__header">
                    <FontAwesomeIcon icon={faUserPen} className="dashboard-panel__icon" />
                    <span className="dashboard-panel__title">Созданные мной</span>
                    <span className="dashboard-panel__count">{createdTasks.length}</span>
                    </div>

                    <div className="dashboard-created">
                    {sortedCreatedTasks.length === 0 && (
                        <div className="dashboard-tasks__empty">
                        Вы ещё не создали ни одной задачи
                        </div>
                    )}

                    {sortedCreatedTasks.map((t) => {
                        const isOverdue = t.finishDate && new Date(t.finishDate) < now;
                        const isToday = t.finishDate && sameDay(t.finishDate, now);
                        const executors = Array.isArray(t.executors)
                        ? t.executors
                        : (t.executor ? [t.executor] : []);

                        return (
                        <div
                            key={t._id}
                            className={`dashboard-created__item ${isOverdue ? 'dashboard-created__item--overdue' : ''}`}
                            onClick={() => openTask(t)}
                        >
                            <div className="dashboard-created__main">
                            <span className="dashboard-created__name">{t.name}</span>
                            {t.description && (
                                <span className="dashboard-created__desc">
                                {t.description.slice(0, 100)}{t.description.length > 100 ? '…' : ''}
                                </span>
                            )}
                            </div>

                            <div className="dashboard-created__meta">
                            {executors.length > 0 ? (
                                <div className="crm-card__executors">
                                {executors.map((uid) => (
                                    <span key={uid} className="crm-card__executor-badge">
                                    {getUserName(uid)}
                                    </span>
                                ))}
                                </div>
                            ) : (
                                <span className="dashboard-created__noexec">Без исполнителя</span>
                            )}

                            <span
                                className="dashboard-created__status"
                                style={{
                                background: `${getStatusColor(t.status)}22`,
                                color: getStatusColor(t.status),
                                borderColor: `${getStatusColor(t.status)}55`,
                                }}
                            >
                                {getStatusName(t.status)}
                            </span>

                            <span className={`dashboard-created__date ${isOverdue ? 'dashboard-created__date--overdue' : ''} ${isToday ? 'dashboard-created__date--today' : ''}`}>
                                <FontAwesomeIcon icon={faClock} />
                                {t.finishDate ? formatDate(t.finishDate) : 'Без срока'}
                            </span>
                            </div>
                        </div>
                        );
                    })}
                    </div>
                </div>
            )}
            {/* Правая колонка — задачи */}
            <div className="dashboard-panel">
              <div className="dashboard-panel__header">
                <FontAwesomeIcon icon={faListCheck} className="dashboard-panel__icon" />
                <span className="dashboard-panel__title">Мои задачи</span>
                <span className="dashboard-panel__count">{myTasks.length}</span>
              </div>

              <div className="dashboard-tasks">
                {sortedMyTasks.length === 0 && (
                  <div className="dashboard-tasks__empty">У вас пока нет назначенных задач</div>
                )}

                {sortedMyTasks.map((t) => {
                  const isOverdue = t.finishDate && new Date(t.finishDate) < now;
                  const isToday = t.finishDate && sameDay(t.finishDate, now);
                  return (
                    <div
                      key={t._id}
                      className={`dashboard-task ${isOverdue ? 'dashboard-task--overdue' : ''} ${isToday ? 'dashboard-task--today' : ''}`}
                      onClick={() => openTask(t)}
                    >
                      <div className="dashboard-task__header">
                        <span className="dashboard-task__name">{t.name}</span>
                        <span
                          className="dashboard-task__status"
                          style={{
                            background: `${getStatusColor(t.status)}22`,
                            color: getStatusColor(t.status),
                            borderColor: `${getStatusColor(t.status)}55`,
                          }}
                        >
                          {getStatusName(t.status)}
                        </span>
                      </div>

                      {t.description && (
                        <p className="dashboard-task__desc">
                          {t.description.slice(0, 140)}{t.description.length > 140 ? '…' : ''}
                        </p>
                      )}

                      <div className="dashboard-task__footer">
                        {t.finishDate ? (
                          <span className={`dashboard-task__date ${isOverdue ? 'dashboard-task__date--overdue' : ''}`}>
                            <FontAwesomeIcon icon={faClock} />
                            {formatDate(t.finishDate)}
                          </span>
                        ) : (
                          <span className="dashboard-task__date dashboard-task__date--none">
                            Без срока
                          </span>
                        )}
                        {t.contractor && (
                          <span className="dashboard-task__contractor">{t.contractor}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ---- Созданные мной ---- */}
            {/* <div className="dashboard-panel dashboard-panel--created">
            <div className="dashboard-panel__header">
                <FontAwesomeIcon icon={faUserPen} className="dashboard-panel__icon" />
                <span className="dashboard-panel__title">Созданные мной</span>
                <span className="dashboard-panel__count">{createdTasks.length}</span>
            </div>

            <div className="dashboard-created">
                {sortedCreatedTasks.length === 0 && (
                <div className="dashboard-tasks__empty">
                    Вы ещё не создали ни одной задачи
                </div>
                )}

                {sortedCreatedTasks.map((t) => {
                const isOverdue = t.finishDate && new Date(t.finishDate) < now;
                const isToday = t.finishDate && sameDay(t.finishDate, now);
                const executors = Array.isArray(t.executors)
                    ? t.executors
                    : (t.executor ? [t.executor] : []);

                return (
                    <div
                    key={t._id}
                    className={`dashboard-created__item ${isOverdue ? 'dashboard-created__item--overdue' : ''}`}
                    onClick={() => openTask(t)}
                    >
                    <div className="dashboard-created__main">
                        <span className="dashboard-created__name">{t.name}</span>
                        {t.description && (
                        <span className="dashboard-created__desc">
                            {t.description.slice(0, 100)}{t.description.length > 100 ? '…' : ''}
                        </span>
                        )}
                    </div>

                    <div className="dashboard-created__meta">
                        {executors.length > 0 ? (
                        <div className="crm-card__executors">
                            {executors.map((uid) => (
                            <span key={uid} className="crm-card__executor-badge">
                                {getUserName(uid)}
                            </span>
                            ))}
                        </div>
                        ) : (
                        <span className="dashboard-created__noexec">Без исполнителя</span>
                        )}

                        <span
                        className="dashboard-created__status"
                        style={{
                            background: `${getStatusColor(t.status)}22`,
                            color: getStatusColor(t.status),
                            borderColor: `${getStatusColor(t.status)}55`,
                        }}
                        >
                        {getStatusName(t.status)}
                        </span>

                        <span className={`dashboard-created__date ${isOverdue ? 'dashboard-created__date--overdue' : ''} ${isToday ? 'dashboard-created__date--today' : ''}`}>
                        <FontAwesomeIcon icon={faClock} />
                        {t.finishDate ? formatDate(t.finishDate) : 'Без срока'}
                        </span>
                    </div>
                    </div>
                );
                })}
            </div>
            </div> */}
        </>
      )}

      {isModalOpen && <ModalWindow task={selectedTask} onClose={closeTask} />}
    </div>
  );
};

export default DashboardPage;